import { useState, useRef, useEffect } from 'react'
import { X, ChevronDown } from 'lucide-react'

interface MultiSelectOption {
  value: string
  label: string
  icon?: string
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  label?: string
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = 'Select...',
  label,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const removeOption = (value: string) => {
    onChange(selected.filter((v) => v !== value))
  }

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-xs font-medium mb-1 text-fn-text-sub">
          {label}
        </label>
      )}
      <button
        type="button"
        className={`w-full flex items-center gap-1 min-h-[36px] px-2 py-1 rounded-md border text-left transition-all bg-fn-input ${ isOpen ? 'ring-2 ring-primary border-transparent' : 'border-fn-border' }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex-1 flex flex-wrap gap-1">
          {selected.length === 0 ? (
            <span className="text-sm text-fn-text-muted">
              {placeholder}
            </span>
          ) : (
            selected.map((value) => {
              const option = options.find((o) => o.value === value)
              return (
                <span
                  key={value}
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-fn-primary-light text-fn-primary"
                >
                  {option?.icon && <span className="text-xs">{option.icon}</span>}
                  {option?.label || value}
                  <button
                    type="button"
                    className="ml-0.5 hover:opacity-70"
                    onClick={(e) => {
                      e.stopPropagation()
                      removeOption(value)
                    }}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )
            })
          )}
        </div>
        <ChevronDown
          className="w-4 h-4 flex-shrink-0 text-fn-text-muted"
        />
      </button>

      {isOpen && (
        <div
          className="absolute z-50 mt-1 w-full rounded-md border border-fn-border overflow-auto max-h-48 bg-fn-card shadow-fn-dropdown"
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors hover:bg-fn-hover"
              onClick={() => toggleOption(option.value)}
            >
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                readOnly
                className="rounded border-tf-border text-fn-primary"
              />
              {option.icon && <span>{option.icon}</span>}
              <span className="text-fn-text">{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
